import * as React from "react";
import { Mail, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/status-badge";

export interface NoticeItemProps {
  recipient: string;
  recipientType: "webmaster" | "hosting-provider" | "registrar" | string;
  sentDate: string;
  status: "sent" | "bounced" | "pending" | "failed" | string;
  onClick?: () => void;
  className?: string;
}

/**
 * NoticeItem - Reusable component for displaying notice/email entries
 *
 * Usage:
 * <NoticeItem
 *   recipient="Webmaster"
 *   recipientType="webmaster"
 *   sentDate="11/20/2025"
 *   status="sent"
 *   onClick={() => handleViewNotice(id)}
 * />
 */
export function NoticeItem({
  recipient,
  sentDate,
  status,
  onClick,
  className,
}: NoticeItemProps) {
  const formattedDate = React.useMemo(() => {
    try {
      const date = new Date(sentDate);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch {
      return sentDate;
    }
  }, [sentDate]);

  // Map status to StatusBadge-compatible format
  const badgeStatus = React.useMemo(() => {
    const statusMap: Record<string, string> = {
      sent: "REMOVED", // Green - success
      bounced: "LIVE", // Red - error
      pending: "BLOCKED", // Gray - waiting
      failed: "LIVE", // Red - error
    };
    return statusMap[status.toLowerCase()] || status;
  }, [status]);

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors",
        onClick && "cursor-pointer",
        className,
      )}
      onClick={onClick}
    >
      <div className="flex-shrink-0">
        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
          <Mail className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{recipient}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Email • {formattedDate}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={badgeStatus} label={status} />
        {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </div>
    </div>
  );
}


